import { useCallback, useSyncExternalStore } from 'react';
import { UndoConflictError, type UndoEntry, type UndoHistoryItem } from '@wiztime/storage';
import { useEffect, useState } from 'react';
import { describeStep, resolveNames, StepView } from '../lib/undoDescribe';
import { stepText } from '../lib/undoText';
import { useTranslation } from 'react-i18next';
import type { UndoSnapshot } from '../app/undoModel';
import { toastError, toastInfo } from '../shell/toastStore';
import { useServices } from './useServices';

export function useUndoSnapshot(): UndoSnapshot {
  const { undo } = useServices();
  return useSyncExternalStore(undo.subscribe, undo.getSnapshot);
}

export interface UndoActions {
  readonly undo: () => Promise<void>;
  readonly redo: () => Promise<void>;
}

/** Undo and redo with user-facing feedback; conflicts become toasts instead of exceptions. */
export function useUndoActions(): UndoActions {
  const { store } = useServices();
  const { t } = useTranslation();

  const report = useCallback(
    (error: unknown): void => {
      if (error instanceof UndoConflictError) {
        toastError(t('error.undoRemote'));
        return;
      }
      toastError(t('error.unknown'));
    },
    [t],
  );

  /** The step's name with its object and resulting state; falls back to the bare name if the lookup fails. */
  const labelOf = useCallback(
    async (label: UndoHistoryItem['label'], entries: readonly UndoEntry[], view: StepView): Promise<string> => {
      try {
        const names = await resolveNames(store.repo, [entries]);
        return stepText(t, label, describeStep(entries, view, names));
      } catch {
        return t(`command.${label}`);
      }
    },
    [store, t],
  );

  const undo = useCallback(async (): Promise<void> => {
    try {
      const outcome = await store.undo();
      if (outcome !== null) toastInfo(t('undo.undone', { label: await labelOf(outcome.label, outcome.entries, StepView.Undone) }));
    } catch (error) {
      report(error);
    }
  }, [store, t, labelOf, report]);

  const redo = useCallback(async (): Promise<void> => {
    try {
      const outcome = await store.redo();
      if (outcome !== null) toastInfo(t('undo.redone', { label: await labelOf(outcome.label, outcome.entries, StepView.Applied) }));
    } catch (error) {
      report(error);
    }
  }, [store, t, labelOf, report]);

  return { undo, redo };
}

/**
 * The history items as lines of text ("check-in · Drink water → ticked"), in the same order.
 * The names are read asynchronously; until they arrive (or if the lookup fails) each line is the
 * command's bare name.
 */
export function useHistoryTexts(items: readonly UndoHistoryItem[]): readonly string[] {
  const { store } = useServices();
  const { t } = useTranslation();
  const [resolved, setResolved] = useState<{ readonly items: readonly UndoHistoryItem[]; readonly texts: readonly string[] } | null>(
    null,
  );
  useEffect(() => {
    let current = true;
    resolveNames(
      store.repo,
      items.map((item) => item.entries),
    )
      .then((names) => {
        if (!current) return;
        setResolved({
          items,
          texts: items.map((item) => stepText(t, item.label, describeStep(item.entries, StepView.Applied, names))),
        });
      })
      .catch(() => undefined);
    return () => {
      current = false;
    };
  }, [store, t, items]);
  return resolved !== null && resolved.items === items ? resolved.texts : items.map((item) => t(`command.${item.label}`));
}
